// Вкладка «Укомплектованность»: сколько людей требуется постами и сколько назначено.
//
// Дельта пишется словами, а не знаком. «−2» человек читает как ошибку или как долг; «не
// хватает двух человек» читается однозначно и сразу говорит, что делать. Ноль тоже пишется
// словами: «укомплектован» — это результат сравнения, а не пустая ячейка (правило 7).

import { RiGroupLine, RiUserAddLine } from '@remixicon/react';
import { Link } from 'react-router';

import type { SiteOut } from '@/shared/api/endpoints/org';
import { countLabel } from '@/shared/text/plural';
import * as Button from '@/shared/ui/button';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';

import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { useStaffingQuery } from '@/shared/org/queries';

import type { SitesRoute } from '../route';
import { openStaffPostHref } from '../route';

const PERSON_FORMS = ['человек', 'человека', 'человек'] as const;
/** Родительный падеж: после «не хватает» слово склоняется иначе, чем при счёте. */
const PERSON_FORMS_GENITIVE = ['человека', 'человек', 'человек'] as const;

export function StaffingTab({ site, route }: { site: SiteOut; route: SitesRoute }) {
  const query = useStaffingQuery(site.id);

  return (
    <div className='flex flex-col gap-4 p-5'>
      <ScreenState
        data={query.data}
        error={query.error}
        subject='укомплектованность объекта'
        isEmpty={() => false}
        onRetry={() => void query.refetch()}
        loading={<Skeleton className='h-24 w-full' />}
        empty={null}
      >
        {(staffing) => (
          <>
            <FieldList>
              <FieldRow
                label='Требуется по постам'
                value={countLabel(staffing.required, PERSON_FORMS)}
                note='сумма «одновременно охранников» по действующим постам объекта'
              />
              <FieldRow
                label='Назначено сегодня'
                value={countLabel(staffing.assigned, PERSON_FORMS)}
                note='люди с живым назначением на этот объект на сегодняшний день'
              />
            </FieldList>
            <div className='flex items-start gap-3 rounded-xl bg-bg-white-0 p-4 ring-1 ring-inset ring-stroke-soft-200'>
              <div className='flex size-10 shrink-0 items-center justify-center rounded-full bg-bg-weak-50'>
                <RiGroupLine className='size-5 text-text-sub-600' />
              </div>
              <div className='flex min-w-0 flex-col gap-2'>
                <p className='text-paragraph-sm text-text-strong-950'>
                  {deltaWords(staffing.required, staffing.assigned)}
                </p>
                {/* Экран, который называет недобор и не даёт его закрыть, — тупик: человек
                    прочитал «не хватает двоих» и пошёл открывать карточки людей по одной.
                    Кнопка стоит РЯДОМ с этой фразой, а не в шапке карточки: она отвечает
                    именно на неё. */}
                {staffing.assigned < staffing.required && (
                  <Button.Root
                    asChild
                    variant='primary'
                    mode='stroke'
                    size='xsmall'
                    className='self-start'
                  >
                    <Link to={openStaffPostHref(route, site.id)}>
                      <Button.Icon as={RiUserAddLine} />
                      Поставить людей на пост
                    </Link>
                  </Button.Root>
                )}
              </div>
            </div>
          </>
        )}
      </ScreenState>
    </div>
  );
}

/** Дельта словами: что именно означает разница и что с ней делать. */
function deltaWords(required: number, assigned: number): string {
  const delta = assigned - required;
  if (delta === 0) {
    return 'Объект укомплектован: назначено ровно столько людей, сколько требуется действующими постами.';
  }
  if (delta < 0) {
    return `Недобор: не хватает ${countLabel(-delta, PERSON_FORMS_GENITIVE)}. Пока недобор не закрыт, посты придётся закрывать переработками уже назначенных.`;
  }
  return `Назначено на ${countLabel(delta, PERSON_FORMS_GENITIVE)} больше, чем требуется постами. Проверьте, не остались ли живыми назначения тех, кто уже ушёл с объекта.`;
}
